import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { fmt, FREE_SHIPPING_THRESHOLD } from '../lib/api';

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 bg-forest/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-cream flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-emerald-950/10">
              <h3 className="font-display font-bold uppercase text-2xl text-forest">
                Your Cart <span className="text-flame">({items.length})</span>
              </h3>
              <button data-testid="cart-drawer-close" onClick={() => setOpen(false)} className="p-2 hover:text-flame transition" aria-label="Close cart">
                <X />
              </button>
            </div>

            {items.length > 0 && (
              <div className="px-6 py-4 bg-pine text-cream">
                <p className="font-mono text-[11px] uppercase tracking-widest mb-2">
                  {remaining > 0 ? `${fmt(remaining)} away from free shipping` : 'Free shipping unlocked — nice.'}
                </p>
                <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-flame rounded-full"
                    animate={{ width: `${progress}%` }}
                    transition={{ type: 'spring', damping: 25 }}
                  />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <p className="font-display text-2xl uppercase font-bold text-forest">Your cart is empty</p>
                  <p className="text-sm text-sub mt-2 mb-6">Add some heavy-duty parts to get rolling.</p>
                  <Link
                    to="/shop"
                    onClick={() => setOpen(false)}
                    data-testid="cart-drawer-shop-link"
                    className="inline-flex items-center gap-2 bg-forest text-cream font-display uppercase font-semibold px-6 py-3 rounded-full hover:bg-flame transition"
                  >
                    Shop Parts <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                items.map((i) => (
                  <div key={i.id} data-testid={`cart-drawer-item-${i.sku}`} className="flex gap-4 bg-white rounded-xl p-3 border border-emerald-950/10">
                    <img src={i.image} alt={i.name} className="w-20 h-20 rounded-lg object-cover bg-forest shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-forest leading-snug truncate">{i.name}</p>
                      <p className="font-mono text-[10px] text-sub uppercase tracking-widest mt-0.5">{i.sku}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 bg-cream rounded-full border border-emerald-950/10">
                          <button data-testid={`cart-item-decrease-${i.sku}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-1.5 hover:text-flame" aria-label="Decrease quantity"><Minus className="w-3.5 h-3.5" /></button>
                          <span data-testid={`cart-item-qty-${i.sku}`} className="text-sm font-semibold w-6 text-center">{i.qty}</span>
                          <button data-testid={`cart-item-increase-${i.sku}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-1.5 hover:text-flame" aria-label="Increase quantity"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        <span className="font-display font-bold text-forest">{fmt(i.price * i.qty)}</span>
                      </div>
                    </div>
                    <button data-testid={`cart-item-remove-${i.sku}`} onClick={() => remove(i.id)} className="p-1 h-fit text-sub hover:text-bad transition" aria-label="Remove item"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-emerald-950/10 px-6 py-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-sub">Subtotal</span>
                  <span data-testid="cart-drawer-subtotal" className="font-display font-bold text-2xl text-forest">{fmt(subtotal)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/cart"
                    onClick={() => setOpen(false)}
                    data-testid="cart-drawer-view-cart"
                    className="text-center font-display uppercase font-semibold py-3 rounded-full border-2 border-forest text-forest hover:bg-forest hover:text-cream transition"
                  >
                    View Cart
                  </Link>
                  <Link
                    to="/checkout"
                    onClick={() => setOpen(false)}
                    data-testid="cart-drawer-checkout"
                    className="text-center font-display uppercase font-semibold py-3 rounded-full bg-flame text-white hover:bg-ember transition"
                  >
                    Checkout
                  </Link>
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
